import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { NgbActiveModal, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { fabric } from "fabric";
import { IText } from "fabric/fabric-impl";
import { Subject } from "rxjs";
import { first } from "rxjs/operators";
import { PictureData } from "src/app/annotation/canvas/canvas.component";
import { FabricImageService } from "src/app/annotation/fabricImageService";
import { TokenService } from "src/app/auth/token.service";
import { ConfirmDialogComponent } from "src/app/shared/confirm-dialog/confirm-dialog.component";
import { DrawingItemTypeEnum } from "src/app/shared/Models/enums/DrawingItemTypeEnum";
import { SurveyPhotoTypeEnum } from "src/app/shared/Models/enums/SurveyPhotoTypeEnum";
import { TagAssetEnum } from "src/app/shared/Models/enums/TagAssetEnum";
import { PhotoObjectDto } from "src/app/shared/Models/write/PhotoObjectDto";
import { DeviceCheckService } from "../../services/device-check.service";
import { SurveyType } from "../../services/photo.service";
import { PictureInfo } from "../photo-and-remark/photo-viewmodel";
import { BottomActions, SubActions, TopActions } from "./editor-enums";
import {
  TextboxEditorAction,
  TextboxEditorComponent,
} from "./textbox/textbox-editor/textbox-editor.component";

const YELLOW = "#ff0";
const GREEN = "#84e6a0";
const RED = "#f00";
const BLACK = "#000";
const TRANSPARENT = "#00000001";
const SMOKEWHITE = "#f5f5f5";

const DEFAULT_COLORS = {
  [SurveyType.Outside]: {
    [SurveyPhotoTypeEnum.Facade]: YELLOW,
    [SurveyPhotoTypeEnum.Detail]: YELLOW,
    [SurveyPhotoTypeEnum.FrontYard]: YELLOW,
    [SurveyPhotoTypeEnum.SolutionDoubt]: YELLOW,
  },
  [SurveyType.Inside]: {
    [SurveyPhotoTypeEnum.TelcoRoom]: GREEN,
    [SurveyPhotoTypeEnum.ProximusEquipment]: YELLOW,
    [SurveyPhotoTypeEnum.GeneralTechnicalInfoPhoto]: YELLOW,
    [SurveyPhotoTypeEnum.ElectricityInTelcoRoom]: YELLOW,
    [SurveyPhotoTypeEnum.RoomAvailableForNewEquipment]: YELLOW,
    [SurveyPhotoTypeEnum.OutsideIntro]: YELLOW,
    [SurveyPhotoTypeEnum.FloorPlan]: YELLOW,
    [SurveyPhotoTypeEnum.Facade]: YELLOW,
    [SurveyPhotoTypeEnum.Underground]: YELLOW,
    [SurveyPhotoTypeEnum.General]: YELLOW,
    [SurveyPhotoTypeEnum.OutsideCopperIntro]: RED,
    [SurveyPhotoTypeEnum.InsideCopperIntro]: RED,
    [SurveyPhotoTypeEnum.OutsideFibreIntro]: YELLOW,
    [SurveyPhotoTypeEnum.InsideFibreIntro]: YELLOW,
  },
};
const DEFAULT_ICONS = {
  [SurveyType.Outside]: {
    [SurveyPhotoTypeEnum.Facade]: [
      TagAssetEnum.DTP_TP,
      TagAssetEnum.BU_TP,
      TagAssetEnum.LU_TP,
      TagAssetEnum.SU_TP,
    ],
    [SurveyPhotoTypeEnum.Detail]: [
      TagAssetEnum.DTP_TP,
      TagAssetEnum.BU_TP,
      TagAssetEnum.LU_TP,
      TagAssetEnum.SU_TP,
    ],
    [SurveyPhotoTypeEnum.SolutionDoubt]: [
      TagAssetEnum.DTP_TP,
      TagAssetEnum.BU_TP,
      TagAssetEnum.LU_TP,
      TagAssetEnum.SU_TP,
    ],
    [SurveyPhotoTypeEnum.FrontYard]: [
      TagAssetEnum.FU_INT_TP,
      TagAssetEnum.EX_INT_TP,
    ],
  },
  [SurveyType.Inside]: {
    [SurveyPhotoTypeEnum.TelcoRoom]: [
      TagAssetEnum.TELCO_TP,
      TagAssetEnum.FU_INT_TP,
      TagAssetEnum.EX_INT_TP,
    ],
    [SurveyPhotoTypeEnum.ProximusEquipment]: [
      TagAssetEnum.TELCO_TP,
      TagAssetEnum.FU_INT_TP,
      TagAssetEnum.EX_INT_TP,
    ],
    [SurveyPhotoTypeEnum.GeneralTechnicalInfoPhoto]: [TagAssetEnum.VS_TP],
    [SurveyPhotoTypeEnum.ElectricityInTelcoRoom]: [TagAssetEnum.TELCO_TP],
    [SurveyPhotoTypeEnum.RoomAvailableForNewEquipment]: [
      TagAssetEnum.BDFO_TP,
      TagAssetEnum.BOX_TP,
      TagAssetEnum.SU_TP,
    ],
    [SurveyPhotoTypeEnum.OutsideIntro]: [
      TagAssetEnum.FU_INT_TP,
      TagAssetEnum.EX_INT_TP,
      TagAssetEnum.DTP_TP,
    ],
    [SurveyPhotoTypeEnum.FloorPlan]: [],
    [SurveyPhotoTypeEnum.Facade]: [
      TagAssetEnum.FU_INT_TP,
      TagAssetEnum.EX_INT_TP,
      TagAssetEnum.DTP_TP,
    ],
    [SurveyPhotoTypeEnum.Underground]: [
      TagAssetEnum.FU_INT_TP,
      TagAssetEnum.EX_INT_TP,
      TagAssetEnum.DTP_TP,
    ],
    [SurveyPhotoTypeEnum.General]: [
      TagAssetEnum.BDFO_TP,
      TagAssetEnum.BOX_TP,
      TagAssetEnum.LU_TP,
      TagAssetEnum.BU_TP,
      TagAssetEnum.SU_TP,
      TagAssetEnum.VS_TP,
    ],
    [SurveyPhotoTypeEnum.OutsideCopperIntro]: [
      TagAssetEnum.FU_INT_TP,
      TagAssetEnum.EX_INT_TP,
    ],
    [SurveyPhotoTypeEnum.InsideCopperIntro]: [
      TagAssetEnum.FU_INT_TP,
      TagAssetEnum.EX_INT_TP,
    ],
    [SurveyPhotoTypeEnum.OutsideFibreIntro]: [
      TagAssetEnum.FU_INT_TP,
      TagAssetEnum.EX_INT_TP,
    ],
    [SurveyPhotoTypeEnum.InsideFibreIntro]: [
      TagAssetEnum.FU_INT_TP,
      TagAssetEnum.EX_INT_TP,
    ],
  },
};

const ICONS = [
  TagAssetEnum.BDFO_TP,
  TagAssetEnum.DTP_TP,
  TagAssetEnum.EX_INT_TP,
  TagAssetEnum.FU_INT_TP,
  TagAssetEnum.BAN_TP,
  TagAssetEnum.ORNA_TP,
  TagAssetEnum.LIGH_TP,
  TagAssetEnum.COAX_TP,
  TagAssetEnum.PXMCU_TP,
  TagAssetEnum.CAB_TP,
  TagAssetEnum.PXMFO_TP,
  TagAssetEnum.VS_TP,
  TagAssetEnum.TS_TP,
  TagAssetEnum.HS_TP,
  TagAssetEnum.BOX_TP,
  TagAssetEnum.FO_TP,
  TagAssetEnum.CU_TP,
  TagAssetEnum.TELCO_TP,
  TagAssetEnum.BU_TP,
  TagAssetEnum.LU_TP,
  TagAssetEnum.SU_TP,
];

const BASE_WIDTH_OFFSET = 0;
const BASE_HEIGHT_OFFSET = 130;
const COMMENT_WIDTH_OFFSET = 384;

@Component({
  selector: "app-image-editor",
  templateUrl: "./image-editor.component.html",
  styleUrls: ["./image-editor.component.scss"],
})
export class ImageEditorComponent implements OnInit, OnDestroy {
  TopActionsEnum = TopActions;
  BottomActionsEnum = BottomActions;
  SubActionsEnum = SubActions;
  topActions = TopActions.Selector;
  bottomActions = BottomActions.Nothing;
  subActions = SubActions.Nothing;
  toolboxVisible = false;
  showColorSelector = false;
  showLineColorSelector = false;
  showLineStrokeWidthSelector = false;
  overwriteShowLineEdit = false;
  selectorVisible = true;
  lineVisible = false;

  possibleStrokeWidths = [2, 4, 6, 8, 10];
  possibleLineColors = [BLACK, YELLOW, GREEN, RED];
  possibleTextColors = [SMOKEWHITE, BLACK, YELLOW, GREEN, RED];
  possibleBackgroundColors = [TRANSPARENT, SMOKEWHITE, BLACK];
  possibleBorderColors = [TRANSPARENT, SMOKEWHITE, BLACK, GREEN, RED];

  lineAddArrowHeads: number = 2;
  strokeDashArray: number[] = [0, 0];
  selectedLineStrokeWidth = 6;
  selectedLineColor = YELLOW;
  selectedTextColor = BLACK;
  selectedTextBackgroundColor = this.possibleBackgroundColors[0];
  selectedTextBorderColor = this.possibleBorderColors[0];

  tags: TagAssetEnum[] = ICONS;
  tagEnum = TagAssetEnum;
  @Input() surveyType: SurveyType;
  @Input() photos: PictureInfo[] = [];
  @Input() selectedPhoto: PictureInfo;
  @Input() remarkFieldFEName: string;
  @Input() fixedIndex: boolean = false;
  @ViewChild("canvasHolder", { static: true })
  canvasHolder: ElementRef<HTMLElement>;
  canvas: HTMLCanvasElement;
  toggledPhotoTabId: DrawingItemTypeEnum = DrawingItemTypeEnum.ImageEdit;
  imageBaseUrl: string = "assets/Keen&Wise_CP_DL_";
  selectedLayer: any;
  onSave: Subject<{ data: PictureData; selectedPhoto: PictureInfo }> =
    new Subject<{ data: PictureData; selectedPhoto: PictureInfo }>();
  onCancel: Subject<number> = new Subject<number>();
  onDelete: Subject<number> = new Subject<number>();
  previousPoint: fabric.Point;
  selectedPhotoIndex: number;
  drawingArrow: boolean = false;
  blockForLoading: boolean = false;

  transparent = TRANSPARENT;
  white = SMOKEWHITE;
  black = BLACK;

  textEditorOpen = false;

  commentOpen = true;
  onPC = false;

  constructor(
    private imageService: FabricImageService,
    private deviceCheckService: DeviceCheckService,
    readonly activeModal: NgbActiveModal,
    private _modalService: NgbModal,
    private el: ElementRef
  ) {}
  ngOnInit(): void {
    this.selectedPhotoIndex = this.selectedPhoto.index;
    this.handleScreenSize();
    this.loadImageOnCanvas(0);
  }

  get scaleWidthOffset() {
    return (
      BASE_WIDTH_OFFSET +
      (this.onPC && this.commentOpen ? COMMENT_WIDTH_OFFSET : 0)
    );
  }

  @HostListener("window:resize")
  onResize() {
    setTimeout(() => {
      this.handleScreenSize();
      this.scaleCanvas();
    }, 120); // IPAD PWA bug where window.innerHeigt is delayed value changes
  }

  private scaleCanvas() {
    this.imageService.scaleToScreen(BASE_HEIGHT_OFFSET, this.scaleWidthOffset);
  }

  private handleScreenSize() {
    this.onPC = this.deviceCheckService.onPC();
    this.el.nativeElement.style.setProperty(
      "--height",
      `${window.innerHeight - (this.onPC ? 0 : 4)}px`
    ); // otherwise you get a scrollbar on mobile :(
  }

  @HostListener("document:keyup", ["$event"])
  handleDeleteKeyboardEvent(event: KeyboardEvent) {
    if (event.key === "Delete" && !this.textEditorOpen)
      this.imageService.removeActiveObject();
  }

  swipeImage(actionOnIndex: number): void {
    if (this.fixedIndex) return;
    this.blockForLoading = true;
    this.save(() => this.loadImageOnCanvas(actionOnIndex));
  }

  loadImageOnCanvas(indexDelta: number) {
    this.selectedPhotoIndex += indexDelta;
    this.selectedPhoto = this.photos[this.selectedPhotoIndex];
    this.loadImg(
      this.selectedPhoto.data.originalBlob ??
        this.selectedPhoto.data.originalUrl,
      () => {
        this.setItems(this.selectedPhoto.data.items).then(() => {
          this.blockForLoading = false;
          this.initialState = this.imageService.undo_stack.slice(-1)[0];
        });
        this.setBackgroundImageMetadata(
          this.selectedPhoto.data.backgroundImageMetadata
        );
        if (this.selectedPhoto.data?.items?.length > 0) {
          this.topActions = TopActions.Selector;
          this.bottomActions = BottomActions.Nothing;
        } else {
          this.topActions = TopActions.Toolbox;
          this.toggledPhotoTabId = DrawingItemTypeEnum.DrawingArrowLine;
          this.bottomActions = BottomActions.Arrow;
          this.lineAddArrowHeads = 0;
        }
        this.selectedLineColor =
          DEFAULT_COLORS[this.surveyType][this.selectedPhoto.data.type];
        this.tags = DEFAULT_ICONS[this.surveyType][this.selectedPhoto.data.type]
          .concat(ICONS)
          .filter((v, i, a) => a.findIndex((x) => x === v) === i);
        this.subActions = SubActions.Nothing;
        this.commentOpen = true;
      }
    );
  }

  private initCanvas() {
    this.discardAllChanges();
    this.imageService.init(this.canvas, this.canvas.width, this.canvas.height);
    this.toggledPhotoTabId = null;
    setTimeout(() => this.initEventListeners(), 500);
  }

  get redoEnabled(): boolean {
    return this.imageService.redoEnabled;
  }

  get undoEnabled(): boolean {
    return this.imageService.undoEnabled;
  }

  get layers(): fabric.Object[] {
    return this.imageService.Layers;
  }

  discardAllChanges() {
    this.imageService.clearState();
    this.canvasHolder.nativeElement.innerHTML = "";
    this.canvas = document.createElement("canvas");

    if (window.innerHeight > window.innerWidth) {
      this.canvas.width = (window.innerWidth / 100) * 95;
      this.canvas.height = (window.innerWidth / 100) * 95;
    } else {
      this.canvas.width = (window.innerHeight / 100) * 70;
      this.canvas.height = (window.innerHeight / 100) * 70;
    }
    this.canvasHolder.nativeElement.appendChild(this.canvas);
  }

  addImage(name: string, imageId: number, type: DrawingItemTypeEnum) {
    if (event && event.target)
      this.imageService.addImage(
        `${this.imageBaseUrl}${DrawingItemTypeEnum[type]}_${TagAssetEnum[imageId]}.svg`,
        imageId,
        type,
        this.selectedTextBackgroundColor,
        name,
        this.onDeselect,
        this.onSelectTagFunction
      );
  }

  get photoRemarks() {
    if (this.selectedPhoto[this.remarkFieldFEName] == undefined)
      this.selectedPhoto[this.remarkFieldFEName] = "";
    return this.selectedPhoto[this.remarkFieldFEName];
  }

  openPhotoComment() {
    if (this.onPC) {
      this.commentOpen = !this.commentOpen;
      this.scaleCanvas();
    } else {
      this.textEditorOpen = true;
      const modalRef = this._modalService.open(TextboxEditorComponent, {
        windowClass: "text-modal-overwrite",
      });
      var text = this.selectedPhoto[this.remarkFieldFEName];
      modalRef.componentInstance.textInput = text
        ? text.endsWith(" ")
          ? text
          : text + " "
        : "";
      modalRef.componentInstance.textOutput
        .pipe(first())
        .subscribe((text: string) => {
          this.textEditorOpen = false;
          this.updateRemarks(text);
        });
    }
  }

  updateRemarks(val: string) {
    this.selectedPhoto[this.remarkFieldFEName] = val.trim();
    this.selectedPhoto.remarkChanged = true;
  }

  textboxActions: () => TextboxEditorAction[] = () =>
    this.possibleTextColors.map((c) => ({
      icon: "palette",
      iconColor: c,
      action: () => {
        this.setTextColor(c);
      },
      isActive: () => this.selectedTextColor == c,
    }));

  openTextBoxCreate() {
    var obj = this.imageService.getActiveObject() as IText;
    if (!obj) {
      this.selectedTextBackgroundColor = this.possibleBackgroundColors[0];
      this.selectedTextBorderColor = this.possibleBorderColors[0];
    }
    this.textEditorOpen = true;
    const modalRef = this._modalService.open(TextboxEditorComponent, {
      windowClass: "text-modal-overwrite",
    });
    var text = obj?.text;
    modalRef.componentInstance.textInput = text
      ? text.endsWith(" ")
        ? text
        : text + " "
      : "";
    modalRef.componentInstance.actions = this.textboxActions();
    modalRef.componentInstance.textOutput
      .pipe(first())
      .subscribe((text: string) => {
        this.textEditorOpen = false;
        if (obj != null) this.imageService.updateTextBoxText(text.trim());
        else
          this.imageService.addTextBox(
            this.onDeselect,
            this.onSelectTextboxFunction,
            text.trim(),
            this.selectedTextColor,
            this.selectedTextBackgroundColor,
            this.selectedTextBorderColor
          );
      });
  }

  addTextBox(text: string) {
    this.imageService.addTextBox(
      this.onDeselect,
      this.onSelectTextboxFunction,
      text,
      this.selectedTextColor,
      this.selectedTextBackgroundColor,
      this.selectedTextBorderColor
    );
  }
  setTextColor(color: string) {
    this.selectedTextColor = color;
    this.imageService.setTextColor(color);
  }

  setTextBackgroundColor(color: string) {
    this.selectedTextBackgroundColor = color;
    this.imageService.setTextBackgroundColor(color);
  }
  setTextBorderColor(color: string) {
    this.selectedTextBorderColor = color;
    this.imageService.setTextBorderColor(color);
  }

  setArrowColor(color: string) {
    this.selectedLineColor = color;
    this.imageService.updateArrowLineOptions(color, null, null, null);
  }

  setArrowStrokeWidth(value: number) {
    this.selectedLineStrokeWidth = value;
    this.imageService.updateArrowLineOptions(null, value, null, null);
  }

  setArrowHead(value: number) {
    this.lineAddArrowHeads = value;
    this.imageService.updateArrowLineOptions(null, null, value, null);
  }

  setArrowDashArray(value: number[]) {
    this.strokeDashArray = value;
    this.imageService.updateArrowLineOptions(null, null, null, value);
  }

  setLineColor(color: string) {
    this.selectedLineColor = color;
    this.imageService.redrawBaseLineGroup(color, null, null, null);
  }

  setLineStrokeWidth(value: number) {
    this.selectedLineStrokeWidth = value;
    this.imageService.redrawBaseLineGroup(null, value, null, null);
  }

  setLineArrowHead(value: number) {
    this.lineAddArrowHeads = value;
    this.imageService.redrawBaseLineGroup(null, null, value, null);
  }

  setLineDashArray(value: number[]) {
    this.strokeDashArray = value;
    this.imageService.redrawBaseLineGroup(null, null, null, value);
  }

  setItems(items: PhotoObjectDto[]) {
    return this.imageService.addItems(
      items,
      this.imageBaseUrl,
      this.onDeselect,
      this.onSelectDrawingArrowLineFunction,
      this.onSelectTextboxFunction,
      this.onSelectDrawingFunction,
      this.onSelectTagFunction,
      this.onSelectCircleFunction
    );
  }

  setBackgroundImageMetadata(metadata: string) {
    if (metadata) {
      const data = JSON.parse(metadata);
      if (data != null) {
        if (data?.zoom != 0) this.imageService.setZoom(data.zoom);

        this.rotate(data.angle);
        if (data.flipX) this.flip(true);

        if (data.flipY) this.flip(false);
      }
    }
  }

  toggleLayerVisibility(layer: fabric.Object, type: DrawingItemTypeEnum) {
    layer.visible = !layer.visible;
    if (type != DrawingItemTypeEnum.Drawing)
      layer.hasRotatingPoint = layer.selectable = layer.visible;

    if (!layer.visible && layer == this.imageService.Canvas.getActiveObject())
      this.imageService.Canvas.discardActiveObject();

    this.imageService.Canvas.requestRenderAll();
    this.imageService.saveState();
  }

  toggleLayerLock(layer: fabric.Object, type: DrawingItemTypeEnum) {
    if (!layer.visible || type == DrawingItemTypeEnum.Drawing) return;

    layer.selectable = layer.hasRotatingPoint = !layer.selectable;

    if (
      !layer.selectable &&
      layer == this.imageService.Canvas.getActiveObject()
    )
      this.imageService.Canvas.discardActiveObject();

    this.imageService.Canvas.requestRenderAll();
    this.imageService.saveState();
  }

  deleteLayer(layer: fabric.Object, index: number) {
    this.imageService.Canvas.remove(layer);
    this.imageService.Canvas.requestRenderAll();
    this.layers.splice(index, 1);
    if (this.toggledPhotoTabId == DrawingItemTypeEnum.Drawing)
      this.selectedLayer = null;

    this.imageService.saveState();
  }

  deleteAllObjectsFromCanvas() {
    const modalRef = this._modalService.open(ConfirmDialogComponent);
    modalRef.componentInstance.message = $localize`:@@validation.photo.aknowledgeclearobjects:Are you sure you want to remove all the drawings from the photo?`;
    modalRef.componentInstance.confirmBtnTxt = "clear";
    modalRef.componentInstance.confirmBtnClass = "btn btn-primary";
    modalRef.componentInstance.cancelBtnClass = "btn btn-primary-no-bg";
    modalRef.componentInstance.decision
      .pipe(first())
      .subscribe((decision: boolean) => {
        if (decision) this.imageService.clearCanvas();
        else return;
      });
  }

  clearActiveObject() {
    this.imageService.deselectActiveObject();
  }

  initialState;

  loadImg(src: File | string, onloadHandler?: () => void) {
    this.initCanvas(); // add src here to set image as background instead of steady
    const onload = (
      service: FabricImageService,
      onloadHandler?: () => void
    ) => {
      this.scaleCanvas();
      service.saveState();
      if (onloadHandler) onloadHandler();
    };
    this.imageService.addBackgroundImage(src, () =>
      onload(this.imageService, onloadHandler)
    );
  }

  rotate(degrees: number) {
    this.imageService.rotate(degrees);
    this.scaleCanvas();
    this.imageService.saveState();
  }

  flip(horizontally: boolean) {
    this.imageService.flip(horizontally);
    this.imageService.saveState();
  }

  zoom(zoomIn: boolean) {
    this.imageService.zoom(zoomIn);
  }

  save(callback?: () => void) {
    if (this.initialState == this.imageService.lastState) {
      if (callback) callback();
    } else {
      this.imageService.getPictureData().then((data) => {
        this.onSave.next({
          data: data,
          selectedPhoto: this.selectedPhoto,
        });
        if (callback) callback();
      });
    }
  }

  close() {
    this.save(() => {
      this.cancel();
      this.activeModal.close();
    });
  }

  cancel() {
    this.onCancel.next(this.selectedPhoto.index);
  }

  deletePhoto() {
    const modalRef = this._modalService.open(ConfirmDialogComponent);
    modalRef.componentInstance.message = $localize`:@@validation.photo.aknowledgedelete:Are you sure you want to delete the photo from LST?`;
    modalRef.componentInstance.confirmBtnTxt = "delete";
    modalRef.componentInstance.confirmBtnClass = "btn btn-danger";
    modalRef.componentInstance.cancelBtnClass = "btn btn-primary-no-bg";
    modalRef.componentInstance.decision
      .pipe(first())
      .subscribe((decision: boolean) => {
        if (decision)
          if (this.photos.length == 1 || this.fixedIndex) {
            this.onDelete.next(this.selectedPhoto.index);
            this.activeModal.close();
          } else {
            this.onDelete.next(this.selectedPhoto.index);
            setTimeout(() => {
              if (this.selectedPhotoIndex != 0) this.loadImageOnCanvas(-1);
              else this.loadImageOnCanvas(0);
            }, 400);
          }
        else return;
      });
  }

  undo() {
    this.imageService.undo();
  }

  redo() {
    this.imageService.redo();
  }

  togglePhotoTab(id: DrawingItemTypeEnum, layer: any) {
    if (id == this.toggledPhotoTabId) {
      this.toggledPhotoTabId = null;
    } else {
      this.selectedLayer = layer;
      this.toggledPhotoTabId = id;
    }
  }

  // visibility actions

  toggleTopAction(value: TopActions) {
    this.imageService.deselectActiveObject();

    if (value == TopActions.Selector || value != this.topActions)
      this.bottomActions = this.BottomActionsEnum.Nothing;
    this.subActions = this.SubActionsEnum.Nothing;
    this.topActions = value;
  }
  toggleBottomAction(value: BottomActions) {
    if (value == this.bottomActions) value = BottomActions.Nothing;
    this.subActions = this.SubActionsEnum.Nothing;
    if (value == BottomActions.Arrow) this.previousPoint = undefined;
    if (value == BottomActions.DrawLine) {
      const line = this.imageService
        .getDrawingGroup()
        ?._objects.find((o) => o.type != "circle");
      if (line) {
        this.selectedLineColor = line.stroke;
        this.selectedLineStrokeWidth = line.strokeWidth;
        this.strokeDashArray = line.strokeDashArray;
      }
    }
    this.bottomActions = value;
  }
  toggleSubAction(value: SubActions) {
    if (value == this.subActions) value = SubActions.Nothing;
    this.subActions = value;
  }

  removeSelectedObject(): void {
    this.imageService.removeActiveObject();
    this.bottomActions = BottomActions.Nothing;
    this.subActions = SubActions.Nothing;
  }

  copySelectedObject(): void {
    this.imageService.copyAndPasteActiveObject(
      this.onDeselect,
      this.onSelectDrawingArrowLineFunction,
      this.onSelectTextboxFunction,
      this.onSelectTagFunction,
      this.onSelectCircleFunction
    );
  }

  ngOnDestroy() {
    this.onSave.complete();
    this.onCancel.complete();
  }

  onDeselect = () => {
    if (this.topActions == TopActions.Selector) {
      this.topActions = TopActions.Selector;
      this.bottomActions = BottomActions.Nothing;
      this.subActions = SubActions.Nothing;
    }
  };

  // on select funtions
  onSelectDrawingArrowLineFunction = (setControls: boolean = true) => {
    if (this.topActions != TopActions.Selector && this.previousPoint != null) {
      this.imageService.deselectActiveObject();
    } else {
      var obj = this.imageService.getActiveObject();
      if (setControls)
        obj.setControlsVisibility({
          bl: false,
          br: false,
          mlArrow: true,
          mrArrow: true,
          mtr: false,
          tlRadius: false,
          brRadius: false,
        } as any);
      this.togglePhotoTab(null, null);
      this.topActions = TopActions.Selector;
      this.bottomActions = BottomActions.Arrow;
      this.subActions = SubActions.LineColor;
      this.lineAddArrowHeads = obj["arrowType"];
      this.selectedLineColor = obj.stroke;
      this.selectedLineStrokeWidth = obj.strokeWidth;
      this.strokeDashArray = obj.strokeDashArray;
      this.imageService.setActiveObject(obj);
    }
  };
  onSelectCircleFunction = () => {
    if (this.topActions != TopActions.Selector && this.previousPoint != null) {
      this.imageService.deselectActiveObject();
    } else {
      var obj = this.imageService.getActiveObject();
      obj.setControlsVisibility({
        bl: false,
        br: false,
        mlArrow: false,
        mrArrow: false,
        mtr: false,
        tlRadius: true,
        brRadius: true,
      } as any);
      this.togglePhotoTab(null, null);
      this.topActions = TopActions.Selector;
      this.bottomActions = BottomActions.Circle;
      this.selectedLineColor = obj.stroke;
      this.selectedLineStrokeWidth = obj.strokeWidth;
      this.strokeDashArray = obj.strokeDashArray;
      this.imageService.setActiveObject(obj);
    }
  };
  onSelectDrawingFunction = () => {
    if (this.topActions != TopActions.Selector && this.previousPoint != null) {
      this.imageService.deselectActiveObject();
    } else {
      var obj = this.imageService.getActiveObject();
      obj.setControlsVisibility({
        mtr: false,
        bl: false,
        br: false,
        mlArrow: false,
        mrArrow: false,
        tlRadius: false,
        brRadius: false,
      } as any);
      this.togglePhotoTab(null, null);
      this.topActions = TopActions.Selector;
      this.bottomActions = BottomActions.DrawLine;
      this.subActions = SubActions.LineColor;
      const line = obj["_objects"].find((o) => o.type == "line");
      this.selectedLineColor = line.stroke;
      this.selectedLineStrokeWidth = line.strokeWidth;
      this.strokeDashArray = line.strokeDashArray;
      this.imageService.setActiveObject(obj);
    }
  };
  onSelectTextboxFunction = () => {
    if (this.topActions != TopActions.Selector && this.previousPoint != null) {
      this.imageService.deselectActiveObject();
    } else {
      var obj = this.imageService.getActiveObject();
      obj.setControlsVisibility({
        mtr: true,
        bl: true,
        br: true,
        mlArrow: false,
        mrArrow: false,
        tlRadius: false,
        brRadius: false,
      } as any);
      this.togglePhotoTab(null, null);
      this.topActions = TopActions.Selector;
      this.bottomActions = BottomActions.Text;
      this.subActions = SubActions.TextColor;
      this.selectedTextBackgroundColor = obj.backgroundColor;
      this.selectedTextBorderColor = obj.cornerColor;
      this.selectedTextColor = obj.fill as string;
      this.imageService.setActiveObject(obj);
    }
  };
  onSelectTagFunction = () => {
    if (this.topActions != TopActions.Selector && this.previousPoint != null) {
      this.imageService.deselectActiveObject();
    } else {
      var obj = this.imageService.getActiveObject();
      obj.setControlsVisibility({
        mtr: true,
        bl: true,
        br: true,
        mlArrow: false,
        mrArrow: false,
        tlRadius: false,
        brRadius: false,
      } as any);
      this.togglePhotoTab(null, null);
      this.topActions = TopActions.Selector;
      this.bottomActions = BottomActions.Icon;
      this.subActions = SubActions.Icon;
      this.imageService.setActiveObject(obj);
    }
  };

  checkActuallyMoved(
    previousPointer: { x: number; y: number },
    newPointer: { x: number; y: number }
  ): boolean {
    var a = previousPointer.x - newPointer.x;
    var b = previousPointer.y - newPointer.y;
    return Math.sqrt(a * a + b * b) > 40;
  }

  initEventListeners() {
    this.imageService.Canvas.on("object:moving", (e: fabric.IEvent) => {
      var obj = e.target;
      obj.transparentCorners = true;
      // if object is too big ignore
      var rect = obj.getBoundingRect();
      if (
        rect.height - obj.padding > obj.canvas.height ||
        rect.width - obj.padding > obj.canvas.width
      )
        return;

      obj.setCoords();
      var rect = obj.getBoundingRect();
      var top = rect.top + obj.padding;
      var left = rect.left + obj.padding;
      var height = rect.height - obj.padding * 2;
      var width = rect.width - obj.padding * 2;
      // top-left  corner
      if (top < 0 || left < 0) {
        obj.top = Math.max(obj.top, obj.top - top);
        obj.left = Math.max(obj.left, obj.left - left);
      }
      // bot-right corner
      if (top + height > obj.canvas.height || left + width > obj.canvas.width) {
        obj.top = Math.min(obj.top, obj.canvas.height - height + obj.top - top);
        obj.left = Math.min(
          obj.left,
          obj.canvas.width - width + obj.left - left
        );
      }
    });
    this.imageService.Canvas.on(
      "object:scaling",
      (e: fabric.IEvent) => (e.target.transparentCorners = true)
    );
    this.imageService.Canvas.on(
      "object:rotating",
      (e: fabric.IEvent) => (e.target.transparentCorners = true)
    );
    this.imageService.Canvas.on("mouse:up", (event: fabric.IEvent) => {
      var activeObj = this.imageService.Canvas.getActiveObject();
      if (activeObj && this.drawingArrow) {
        this.drawingArrow = false;
        this.imageService.Canvas.remove(activeObj);
        const pointer = this.imageService.Canvas.getPointer(event.e);
        if (pointer.x < 0) pointer.x = 0;
        if (pointer.y < 0) pointer.y = 0;
        if (pointer.x > this.imageService.Canvas.width)
          pointer.x = this.imageService.Canvas.width - 1;
        if (pointer.y > this.imageService.Canvas.height)
          pointer.y = this.imageService.Canvas.height - 1;
        if (
          this.previousPoint &&
          this.checkActuallyMoved(this.previousPoint, pointer)
        ) {
          var arrowobj = this.imageService.drawArrowLine(
            this.onDeselect,
            this.onSelectDrawingArrowLineFunction,
            this.previousPoint.x,
            this.previousPoint.y,
            pointer.x,
            pointer.y,
            this.selectedLineColor,
            this.selectedLineStrokeWidth,
            this.lineAddArrowHeads,
            this.strokeDashArray
          );
          this.previousPoint = undefined;
          // this.imageService.setActiveObject(arrowobj);
          this.imageService.Canvas.renderAll();
        }
        this.topActions = TopActions.Toolbox;
        this.bottomActions = BottomActions.Arrow;
        this.togglePhotoTab(6, null);
      }
      if (activeObj) {
        activeObj.transparentCorners = false;
        this.imageService.Canvas.renderAll();
      }
    });

    this.imageService.Canvas.on("mouse:move", (event: fabric.IEvent) => {
      if (this.drawingArrow) {
        var activeObj =
          this.imageService.Canvas.getActiveObject() as fabric.Line;
        const pointer = this.imageService.Canvas.getPointer(event.e);
        if (pointer.x < 0) pointer.x = 0;
        if (pointer.y < 0) pointer.y = 0;
        if (pointer.x > this.imageService.Canvas.width)
          pointer.x = this.imageService.Canvas.width - 1;
        if (pointer.y > this.imageService.Canvas.height)
          pointer.y = this.imageService.Canvas.height - 1;
        activeObj.set({
          x2: pointer.x,
          y2: pointer.y,
        });
        activeObj.setCoords();
        this.imageService.Canvas.renderAll();
      }
    });

    this.imageService.Canvas.on("mouse:down", (event: fabric.IEvent) => {
      const pointer = this.imageService.Canvas.getPointer(event.e);
      if (this.toggledPhotoTabId == DrawingItemTypeEnum.DrawingArrowLine) {
        if (this.previousPoint) {
          var arrowObj = this.imageService.drawArrowLine(
            this.onDeselect,
            this.onSelectDrawingArrowLineFunction,
            this.previousPoint.x,
            this.previousPoint.y,
            pointer.x,
            pointer.y,
            this.selectedLineColor,
            this.selectedLineStrokeWidth,
            this.lineAddArrowHeads,
            this.strokeDashArray
          );
          this.imageService.setActiveObject(arrowObj);
          this.drawingArrow = false;
          this.previousPoint = undefined;
        } else {
          var arrowObj = this.imageService.drawArrowLine(
            this.onDeselect,
            this.onSelectDrawingArrowLineFunction,
            pointer.x,
            pointer.y,
            pointer.x,
            pointer.y,
            this.selectedLineColor,
            this.selectedLineStrokeWidth,
            this.lineAddArrowHeads,
            this.strokeDashArray,
            false
          );
          arrowObj.setControlsVisibility({
            bl: false,
            br: false,
            mlArrow: false,
            mrArrow: false,
            mtr: false,
            tlRadius: false,
            brRadius: false,
          });
          this.imageService.setActiveObject(arrowObj);
          this.drawingArrow = true;
          this.previousPoint = new fabric.Point(pointer.x, pointer.y);
        }
      } else if (this.toggledPhotoTabId == DrawingItemTypeEnum.Drawing) {
        this.selectedLayer = this.imageService.getDrawingGroup();
        if (!this.selectedLayer)
          this.selectedLayer = new fabric.Group([], {
            selectable: true,
            hasBorders: true,
            hasRotatingPoint: false,
          });
        var group = this.selectedLayer;
        group.on("selected", () => this.onSelectDrawingFunction());
        group.on("deselected", () => this.onDeselect());

        this.imageService.drawLine(
          this.selectedLayer,
          pointer.x,
          pointer.y,
          this.selectedLayer._sequence,
          this.selectedLineColor,
          this.selectedLineStrokeWidth,
          this.strokeDashArray
        );
      } else if (this.toggledPhotoTabId == DrawingItemTypeEnum.Circle) {
        this.imageService.drawCircle(
          this.onDeselect,
          this.onSelectCircleFunction,
          pointer.x,
          pointer.y,
          80,
          "rgba(0,0,0,0)",
          this.selectedLineColor,
          this.selectedLineStrokeWidth,
          this.strokeDashArray
        );
      }
    });
  }
}
